import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getLogos } from '@/utils/api';
import { formatDocument } from '@/utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Wallet, Loader2 } from 'lucide-react';
import InstallPWA from '@/components/InstallPWA';
import TwoFactorVerify from '@/components/TwoFactorVerify';

const WHATSAPP_SUPPORT = process.env.REACT_APP_WHATSAPP_SUPPORT || '+18297091155';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [cedula, setCedula] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginLogo, setLoginLogo] = useState('/recargameya-logo.jpg');
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const { login, loginWith2FA } = useAuth();
  const navigate = useNavigate();

  const handleCedulaChange = (e) => {
    const formatted = formatDocument(e.target.value);
    setCedula(formatted);
  };

  useEffect(() => {
    // Load custom login logo
    getLogos()
      .then(logos => {
        if (logos.login_logo) {
          setLoginLogo(`${BACKEND_URL}/api/uploads/logos/${logos.login_logo}`);
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(cedula, password);
    
    if (result.requires2FA) {
      // User needs to verify 2FA
      setTwoFAData({
        cedula: cedula,
        tempToken: result.tempToken,
        emailHint: result.emailHint
      });
      setRequires2FA(true);
    } else if (result.success) {
      toast.success('¡Bienvenido a RecargameYa!');
      navigate('/dashboard');
    } else {
      // Tailor the toast to the error kind for clearer feedback on slow networks
      if (result.errorKind === 'timeout') {
        toast.error(result.error, { duration: 6000 });
      } else if (result.errorKind === 'offline' || result.errorKind === 'network') {
        toast.error(result.error, { duration: 6000 });
      } else {
        toast.error(result.error);
      }
    }
    
    setLoading(false);
  };

  const handle2FASuccess = (result) => {
    // Complete login after 2FA verification
    loginWith2FA(result);
    toast.success('¡Bienvenido a RecargameYa!');
    navigate('/dashboard');
  };

  const handle2FACancel = () => {
    setRequires2FA(false);
    setTwoFAData(null);
    setCedula('');
    setPassword('');
  };

  // Show 2FA verification screen
  if (requires2FA && twoFAData) {
    return (
      <TwoFactorVerify
        cedula={twoFAData.cedula}
        tempToken={twoFAData.tempToken}
        emailHint={twoFAData.emailHint}
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md animate-fadeIn">
        {/* PWA Install Banner */}
        <div className="mb-4">
          <InstallPWA variant="banner" />
        </div>
        
        {/* Unified Card with Logo and Form */}
        <Card className="glass shadow-xl overflow-hidden" data-testid="login-card">
          {/* Logo Section inside Card */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-4 flex flex-col items-center border-b">
            <img 
              src={loginLogo} 
              alt="RecargameYa" 
              className="w-full max-w-[448px] rounded-xl shadow-md"
              onError={(e) => { e.target.src = '/recargameya-logo.jpg'; }}
            />
            <p className="text-slate-600 text-sm mt-3 font-medium">Tu Solución en Recargas, Pagos y Servicios</p>
          </div>
          
          <CardHeader className="pb-2">
            <CardTitle data-testid="login-card-title">Entrar al Sistema</CardTitle>
            <CardDescription>Ingresa tu cédula y contraseña para continuar</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cedula">Cédula / Pasaporte</Label>
                <Input
                  id="cedula"
                  type="text"
                  placeholder="000-0000000-0 o Pasaporte"
                  value={cedula}
                  onChange={handleCedulaChange}
                  maxLength={15}
                  required
                  data-testid="login-cedula-input"
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  disabled={loading}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" 
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando al sistema...
                  </>
                ) : (
                  'Entrar al Sistema'
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <p className="text-sm text-slate-600">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="login-register-link">
                Regístrate aquí
              </Link>
            </p>
            <p className="text-xs text-slate-500 text-center">
              ¿Problemas?{' '}
              <a 
                href={`https://wa.me/${WHATSAPP_SUPPORT.replace('+', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Contáctanos por WhatsApp: {WHATSAPP_SUPPORT}
              </a>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Login;
